import { useState } from 'react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import type { AxiosError } from 'axios';
import { useNotification } from '@/context/notification';
import { useDeleteAcademicStatus } from '@/data/learning/academic-situation';
import {
  type AcademicSituationDetails,
  isIntegratedSituation,
} from '@/models/learning/academic-situation';
import { TimelineItem } from './timeline-item';

interface AcademicTimelineProps {
  situation: AcademicSituationDetails;
}

export const AcademicTimeline = ({ situation }: AcademicTimelineProps) => {
  const { toast } = useNotification();
  const [isExpanded, setIsExpanded] = useState(true);
  const { mutate: deleteStatus } = useDeleteAcademicStatus();

  const isIntegrated = isIntegratedSituation(situation);

  const orderedStatuses = [...situation.statuses].sort(
    (first, second) =>
      new Date(second.occurred_at).getTime() -
      new Date(first.occurred_at).getTime()
  );

  const handleDelete = (statusId: number) => {
    deleteStatus(
      { situationId: situation.id, statusId },
      {
        onSuccess: () => {
          toast({
            title: 'Etapa removida',
            content: 'A etapa foi removida da sua linha do tempo.',
            type: 'success',
          });
        },
        onError: (error) => {
          toast({
            title: 'Erro',
            content:
              (error as AxiosError<{ errors?: string[] }>).response?.data
                ?.errors?.[0] || 'Não foi possível remover a etapa.',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box>
          <Typography variant="h2">Linha do tempo acadêmica</Typography>
          <Typography color="neutral.600" mt={0.5}>
            Atualize seu progresso conforme avança e mantenha os documentos
            organizados.
          </Typography>
        </Box>
        <IconButton
          aria-label={isExpanded ? 'Recolher' : 'Expandir'}
          onClick={() => setIsExpanded((value) => !value)}
        >
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Stack>

      <Collapse in={isExpanded}>
        <Stack mt={3}>
          {orderedStatuses.map((status, index) => (
            <TimelineItem
              key={status.id}
              status={status}
              isLast={index === orderedStatuses.length - 1}
              canDelete={!isIntegrated}
              onDelete={handleDelete}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};
